import { inject } from '@vercel/analytics';
import '../sass/main.scss';
import './styles.css';
import canvasDotsBg from '../bgCanvas.js';
import setupMobileNav from '../nav.js';
import projectsData from '../config/projects';
import researchData from '../config/research';

// Initialize Vercel Analytics
inject();

const allProjects = [...researchData, ...projectsData];

window.onload = function () {
  canvasDotsBg();
  setupMobileNav();
  renderProject();
  setupScrollReveal();
};

function renderProject() {
  const params = new URLSearchParams(window.location.search);
  const id = Number(params.get('id'));
  const project = allProjects.find((p) => p.id === id);
  const section = document.getElementById('proj-detail');

  if (!project || !project.detail) {
    section.innerHTML = `
      <div class="proj-detail__not-found">
        <h1>Project not found</h1>
        <a href="/projects">Back to Projects</a>
      </div>`;
    return;
  }

  document.title = `${project.title} | Jack Thoene`;
  const d = project.detail;
  const hasGit = project.gitUrl && project.gitUrl !== '/';

  const ICONS = {
    github: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>',
    cube: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>',
    play: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>',
    external: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>',
  };

  const linkButtons = (d.links && d.links.length > 0)
    ? `<div class="proj-detail__links">${d.links.map((link) => `<a href="${link.url}" target="_blank" rel="noopener noreferrer" class="proj-detail__link-btn">${ICONS[link.icon] || ICONS.external}${link.label}</a>`).join('')}</div>`
    : (hasGit ? `<a href="${project.gitUrl}" target="_blank" rel="noopener noreferrer" class="proj-detail__github-link">${ICONS.github}View on GitHub</a>` : '');

  section.innerHTML = `
    <div class="proj-detail__header reveal">
      <div class="proj-detail__header-text">
        <div class="proj-detail__course">${project.course || ''}</div>
        <h1 class="proj-detail__title">${project.title}</h1>
        <p class="proj-detail__description">${project.description}</p>
        ${linkButtons}
      </div>
    </div>

    <div class="proj-detail__body">
      <div class="proj-detail__overview reveal reveal--delay-1">
        <h2>Overview</h2>
        <p>${d.overview}</p>
      </div>

      <div class="proj-detail__tech reveal reveal--delay-2">
        <h2>Tech Stack</h2>
        <div class="proj-detail__tech-chips">
          ${d.tech.map((t) => `<span class="proj-detail__chip">${t}</span>`).join('')}
        </div>
      </div>

      <div class="proj-detail__highlights reveal reveal--delay-3">
        <h2>Highlights</h2>
        <ul>
          ${d.highlights.map((h) => `<li>${h}</li>`).join('')}
        </ul>
      </div>

      ${d.story && d.story.length > 0 ? `
      <div class="proj-detail__story">
        ${d.story.map((s, i) => `
        <div class="proj-detail__story-section reveal reveal--delay-${(i % 3) + 1}">
          <h2>${s.heading}</h2>
          <p>${s.text}</p>
          ${s.image ? `<div class="proj-detail__story-img"><img src="${s.image}" alt="${s.heading}" loading="lazy" decoding="async" />${s.caption ? `<span class="proj-detail__story-caption">${s.caption}</span>` : ''}</div>` : ''}
        </div>`).join('')}
      </div>` : ''}

      ${d.downloads && d.downloads.length > 0 ? `
      <div class="proj-detail__downloads reveal reveal--delay-4">
        <h2>Documents</h2>
        <div class="proj-detail__download-btns">
          ${d.downloads.map((dl) => `<a href="${dl.url}" target="_blank" rel="noopener noreferrer" class="proj-detail__download-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            ${dl.label}
          </a>`).join('')}
        </div>
      </div>` : ''}
    </div>

    ${d.images && d.images.length > 0 ? `
      <div class="proj-detail__inline-images">
        <h2 class="reveal">Gallery</h2>
        <div class="proj-detail__image-flow">
          ${d.images.map((img, i) => `<div class="proj-detail__flow-img reveal"><img src="${img}" alt="${project.title} — ${i + 1}" loading="lazy" decoding="async" /></div>`).join('')}
        </div>
      </div>` : ''}
    </div>

    <a href="/projects" class="proj-detail__back">&larr; All Projects</a>`;
}

function setupScrollReveal() {
  const reveals = document.querySelectorAll('.reveal, .reveal-left, .reveal-right');
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal--visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { root: null, rootMargin: '0px', threshold: 0.15 }
  );
  reveals.forEach((el) => observer.observe(el));
}
